/**
 * Pure artefact-preview helpers (no DOM / React) — shared by ArtefactPreview
 * and unit tests.
 */

export interface ChecklistItem {
  text: string;
  done: boolean;
}

export function parseChecklistFromMd(bodyMd: string): ChecklistItem[] | null {
  const match = /```(?:json\s+)?checklist\s*\n([\s\S]*?)```/i.exec(bodyMd);
  if (!match?.[1]) return null;
  try {
    const parsed = JSON.parse(match[1]) as { items?: Array<{ text?: string; done?: boolean }> };
    if (!Array.isArray(parsed.items)) return null;
    return parsed.items
      .filter((i) => typeof i.text === "string" && i.text.trim())
      .map((i) => ({ text: i.text!.trim(), done: Boolean(i.done) }));
  } catch {
    return null;
  }
}

/** Strip the checklist fence so prose below doesn't duplicate the interactive list. */
export function stripChecklistFence(bodyMd: string): string {
  return bodyMd.replace(/```(?:json\s+)?checklist\s*\n[\s\S]*?```/gi, "").trim();
}

type ArtefactSection = { heading: string; body: string };

/**
 * Split markdown into ## sections for brief/memo/faq chrome.
 * Leading text before the first heading becomes an untitled lead section.
 */
export function parseArtefactSections(bodyMd: string): ArtefactSection[] {
  const lines = bodyMd.replace(/\r\n/g, "\n").split("\n");
  const sections: ArtefactSection[] = [];
  let heading = "";
  let buf: string[] = [];

  const flush = () => {
    const body = buf.join("\n").trim();
    if (!heading && !body) return;
    sections.push({ heading: heading || "Overview", body });
    heading = "";
    buf = [];
  };

  for (const line of lines) {
    const m = /^#{1,3}\s+(.+)$/.exec(line);
    if (m) {
      flush();
      heading = m[1]!.trim();
      continue;
    }
    buf.push(line);
  }
  flush();
  return sections.length > 0 ? sections : [{ heading: "Overview", body: bodyMd.trim() }];
}
